import { useState, useEffect, useMemo } from "react";
import { useTooltipMode } from "../context/TooltipModeContext";
import CustomTooltip from "../ui/CustomTooltip";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";

import "./PipelineMetrics.css";

// Validar número >= 0
const isValidNumber = (value) =>
  typeof value === "number" && !isNaN(value) && value >= 0;

function fmtNum(v) {
  if (typeof v !== "number" || isNaN(v)) return "—";
  return new Intl.NumberFormat("es-CL", { maximumFractionDigits: 1 }).format(v);
}

export default function PipelineMetrics({ bomba, tooltip, modeAuto, pipelineLarge, cantidad_bombas, mainMetric }) {
  const [o2Status, setO2Status] = useState("rgb(168, 131, 38)");

  const { mode } = useTooltipMode();
  const [modo, setModo] = useState(false)
  const ManualAuto = Boolean(bomba.InStart);
  const [selected, setSelected] = useState("off");

  
  // existeFlujometro
  const existeFlujometro = bomba?.Mass_Flowmeter_Oxigeno !== undefined && bomba?.Mass_Flowmeter_Oxigeno !== null;

  // Sincroniza el knob con el estado real de la bomba
  useEffect(() => {
    if (modeAuto) {
      setSelected("auto");
    } else {
      if (ManualAuto) {
        setSelected("manual");
      } else {
        setSelected("off");
      }
    }
  }, [modeAuto, ManualAuto]);


  useEffect(() => {
    setModo(mode === "detailed");
  }, [mode]);

  // Función para determinar el valor de O2
  const getO2Value = () => {
    // 1. Jerarquía Máxima: Tooltip
    // Si el tooltip tiene un valor válido, úsalo y termina aquí.
    if (isValidNumber(tooltip?.O2)) {
      // console.log("mostrando tooltip")
      return tooltip.O2;
    }

    // 2. Sensor Principal (Lógica Nueva)
    // Para usar el sensor principal, debe ser un número válido Y la bandera de validez debe ser true.
    const isMainSensorReliable = isValidNumber(bomba.O2) && bomba.Measurement_validity;

    if (isMainSensorReliable) {
      // console.log("o2 valido, mostrando valor de o2")
      return bomba.O2;
    }

    // 3. Sensor de Respaldo
    // Si llegamos aquí, es porque el tooltip no existía y el sensor principal falló (por número inválido o por validity false).
    if (isValidNumber(bomba.O2_bckp)) {
      // console.log("o2 invalido , mostrando valor de backup")
      return bomba.O2_bckp;
    }

    // 4. Fallback final
    return "--";
  };

    // Función para determinar el valor de O2
  const getSO2Value = () => {
    // 1. Jerarquía Máxima: Tooltip
    // Si el tooltip tiene un valor válido, úsalo y termina aquí.
    if (isValidNumber(tooltip?.SO2)) {
      // console.log("mostrando tooltip")
      return tooltip.SO2;
    }

    // 2. Sensor Principal (Lógica Nueva)
    // Para usar el sensor principal, debe ser un número válido Y la bandera de validez debe ser true.
    const isMainSensorReliable = isValidNumber(bomba.SO2) && bomba.Measurement_validity;

    if (isMainSensorReliable) {
      // console.log("o2 valido, mostrando valor de o2")
      return bomba.SO2;
    }

    // 3. Sensor de Respaldo
    // Si llegamos aquí, es porque el tooltip no existía y el sensor principal falló (por número inválido o por validity false).
    if (isValidNumber(bomba.SO2_bckp)) {
      // console.log("o2 invalido , mostrando valor de backup")
      return bomba.SO2_bckp;
    }

    // 4. Fallback final
    return "--";
  };



  const Flujometro = isValidNumber(tooltip?.Mass_Flowmeter_Oxigeno)
  ? tooltip.Mass_Flowmeter_Oxigeno
  : isValidNumber(bomba.Mass_Flowmeter_Oxigeno)
  ? bomba.Mass_Flowmeter_Oxigeno
  : isValidNumber(bomba.Mass_Flowmeter_Oxigeno)
  ? bomba.Mass_Flowmeter_Oxigeno
  : isValidNumber(bomba.Mass_Flowmeter_Oxigeno)
  ? bomba.Mass_Flowmeter_Oxigeno
  : isValidNumber(bomba.Mass_Flowmeter_Oxigeno)
  ? bomba.Mass_Flowmeter_Oxigeno
  : "--";

  // const SO2 = isValidNumber(tooltip?.SO2)
  // ? tooltip.SO2
  // : isValidNumber(bomba.SO2)
  // ? bomba.SO2
  // : isValidNumber(bomba.SO2_bckp)
  // ? bomba.SO2_bckp
  // : "--";

  // const O2 = isValidNumber(tooltip?.O2)
  // ? tooltip.O2
  // : isValidNumber(bomba.O2)
  // ? bomba.O2
  // : isValidNumber(bomba.O2_bckp)
  // ? bomba.O2_bckp
  // : "--";
  const O2 = getO2Value();
  const SO2 = getSO2Value();

  const Temp = isValidNumber(tooltip?.Temp)
  ? tooltip.Temp
  : isValidNumber(bomba.TEMP)
  ? bomba.TEMP
  : isValidNumber(bomba.TEMP_bckp)
  ? bomba.TEMP_bckp
  : "--";

// console.log('tooltip', bomba)

  const lm = bomba?.latest_measurements || {};
  const lm_O2 = lm?.O2?.filter((v) => v !== null) || [];

  const lastO2 = lm_O2.at(-1) ?? bomba.O2 ?? 0;
  const prevO2 = lm_O2.at(0) ?? lastO2;

  const diff = lastO2 - prevO2;
  const trend =
    diff > 0.05 ? "up" : diff < -0.05 ? "down" : "flat";

  const setPoint = Number(bomba?.SetPoint ?? 0);
  const setPointOff = Number(bomba?.SetPointOFF ?? 0);

  // === Estado respecto a SetPoints ===
  let statusText = "—";
  let statusColor = "#aaa";
  let DiffIcon = TrendingFlatIcon;

  if (lastO2 < setPoint) {
    statusText = "Inyectando O₂";
    statusColor = "#f02a2a"; // rojo
    DiffIcon = ArrowDownwardRoundedIcon;
  } else if (lastO2 <= setPointOff) {
    statusText = "En rango";
    statusColor = "#a88326"; // amarillo
    DiffIcon = TrendingFlatIcon;
  } else {
    statusText = "Sobre SetPointOFF";
    statusColor = "#296f4d"; // verde
    DiffIcon = ArrowUpwardRoundedIcon;
  }

  // === Tendencia temporal general ===
  let DeltaIcon = null;
  let deltaColor = "#aaa";

  if (trend === "up") {
    DeltaIcon = <TrendingUpIcon style={{ color: "#00c853", fontSize: "1vw" }} />;
    deltaColor = "#00c853";
  } else if (trend === "down") {
    DeltaIcon = <TrendingDownIcon style={{ color: "#f02a2a", fontSize: "1vw" }} />;
    deltaColor = "#f02a2a";
  } else {
    DeltaIcon = <TrendingFlatIcon style={{ color: "#a88326", fontSize: "1vw" }} />;
    deltaColor = "#a88326";
  }



  const metricO2Element = (
    <h2 className="metric-value" style={{ color: o2Status }}>
      {O2 === "--" ? "--" : <>{O2}<span className="caption-unit">mg/L</span></>}
    </h2>
  );

  const metricSO2Element = (
    <h2 className="metric-value" style={{ color: o2Status }}>
      {SO2 === "--" ? "--" : <>{SO2}<span className="caption-unit">%</span></>}
    </h2>
  );

  const metricTempElement = (
    <h2 className="metric-value" style={{ color: o2Status }}>
      {Temp ?? "--"} 
      {Temp && <span className="caption-unit">°C</span>}
    </h2>
  );

  const metricFlujoElement = existeFlujometro && (
    <h2 className="metric-value" style={{ color: o2Status }}>
      {Flujometro === "--" ? "--" : <>{Flujometro}<span className="caption-unit">g/min</span></>}
    </h2>
  );


  let topMetric = metricO2Element;
  let bottomMetrics = [metricSO2Element, metricFlujoElement];

  if (mainMetric === "SO2") {
    topMetric = metricSO2Element;
    bottomMetrics = [metricO2Element, metricFlujoElement];
  }

  if (mainMetric === "Temp") {
    topMetric = metricTempElement;
    bottomMetrics = [metricO2Element, metricFlujoElement];
  }




  useEffect(() => {
    if (!bomba) return;

    const o2 = isValidNumber(bomba.O2) ? bomba.O2 : 0;
    const o2_bckp = isValidNumber(bomba.O2_bckp) ? bomba.O2_bckp : 0;
    const sp = isValidNumber(bomba.SetPoint) ? bomba.SetPoint : 1;
    const spOff = isValidNumber(bomba.SetPointOFF) ? bomba.SetPointOFF : 1;

    const evalStatus = (val) => {
      if (val < sp) return "rgb(240, 42, 42)"; // rojo
      else if (val <= spOff) return "rgb(168, 131, 38)"; // amarillo
      else return "rgb(41, 111, 77)"; // verde
    };

    if (o2 <= 0) {
      setO2Status(evalStatus(o2_bckp));
    } else {
      setO2Status(evalStatus(o2));
    }
  }, [bomba]);

// console.log('mode',mode)
// mil sin decimales cienmil  se pasa a tonelada con 2 decimales 
  return (
    <div
      className={`pipeline-metrics ${
        // cantidad_bombas === 1 ? "one-row" :
         "two-rows"
      }`}
      style={{marginTop: pipelineLarge ? '2vh' : '-0.5vh',}}
    >
        <>
          <div className="top-row">
            <div className="container-metric-value">
              {topMetric}
            </div>
          </div>

          <div className="bottom-row">
            {bottomMetrics.map((m, i) => (
              m && (
                <div key={i} className="container-metric-value">
                  {m}
                </div>
              )
            ))}
          </div>
        </>
      {/*  )} */}

      <DarkKnobSelector
        selected={selected}
        onChange={setSelected}
        cantidad_bombas={cantidad_bombas}
      />
    </div>

    // <div className="pipeline-metrics">
    //   <div className="container-metric-value">
    //     <h2
    //       id={`${id}-main`}
    //       className="metric-value"
    //       style={{ color: o2Status,  }}
    //     >
    //       {O2 === "--" ? ( "--" ) : ( 
    //         <>
    //           {O2}
    //           <span className="caption-unit">mg/L</span>
    //         </>
    //       )}
    //     </h2>
    //   </div>
    //   <div className="container-metric-value">
    //     <h2
    //       id={`${id}-main`}
    //       className="metric-value"
    //       style={{ color: o2Status,  }}
    //     >
    //       {SO2 === "--" ? ( "--" ) : ( 
    //         <>
    //           {SO2}
    //           <span className="caption-unit">%</span>
    //         </>
    //       )}
    //     </h2>
    //   </div>
    //   <div className="container-metric-value">
    //     <h2
    //       id={`${id}-main`}
    //       className="metric-value"
    //       style={{ color: o2Status,  }}
    //     >
    //       {Flujometro === "--" ? ( "--" ) : ( 
    //         <>
    //           {Flujometro}
    //           <span className="caption-unit">%</span>
    //         </>
    //       )}
    //     </h2>
    //   </div>
    //   <DarkKnobSelector selected={selected} onChange={setSelected} cantidad_bombas={cantidad_bombas}/>

     
    // </div>
  );
}



function DarkKnobSelector({ selected, onChange, cantidad_bombas }) {
  const rotation = {
    auto: "-60deg",
    off: "0deg",
    manual: "60deg",
  };

  return (

    // <CustomTooltip
    //   variant={'pipeline'}
    //   title={`A = Automático\nOff = Apagado\nM = Encendido Manualmente`}
    //   color={'#c3c3c3'}
    //   arrow
    // >
    <div className="radio-input" style={{marginLeft: cantidad_bombas === 1 && '6vw', marginTop: cantidad_bombas === 1 && '0.6vh'}}>

      <CustomTooltip
        variant={'pipeline'}
        title={`A = Automático\n Off = Apagado Manualmente\n On = Encendido Manualmente`}
        color={'#c3c3c3'}
        arrow
      >
        <div className="radio-input-path">
          <div className="radioContainer radioContainer1">
            <label>
              <input
                type="radio"
                name="mode"
                value="auto"
                checked={selected === "auto"}
                readOnly
                // onChange={(e) => onChange(e.target.value)}
              />
              A
            </label>
          </div>

          <div className="radioContainer radioContainer3">
            <label>
              <input
                type="radio"
                name="mode"
                value="off"
                checked={selected === "off"}
                readOnly
                // onChange={(e) => onChange(e.target.value)}
              />
              Off
            </label>
          </div>

          <div className="radioContainer radioContainer5">
            <label>
              <input
                type="radio"
                name="mode"
                value="manual"
                checked={selected === "manual"}
                readOnly
                // onChange={(e) => onChange(e.target.value)}
              />
              On
              {/* M */}
            </label>
          </div>
        </div>
      </CustomTooltip>

      <div
        className="knob"
        style={{ transform: `translateX(-50%) rotateZ(${rotation[selected]})` }}
      >
        <div className="center"></div>
      </div>
    </div>        
    // </CustomTooltip>

  );
}